import {addDays, startOfToday} from 'date-fns'

import {toApiDateString} from 'src/jobs/job-schedule-format'
import type {JobListStatusFilter} from 'src/jobs/schemas'
import {useJobs} from 'src/jobs/use-jobs'

import {computeDayOverDayComparison} from './dashboard-comparison'
import type {DashboardStatCardData} from './types'
import {useFleetUtilization} from './use-fleet-utilization'

const ACTIVE_JOB_STATUSES: JobListStatusFilter[] = ['planned', 'active', 'completed']

function jobFiltersForDay(day: Date) {
    const date = toApiDateString(day)
    return {
        startDateFrom: date,
        startDateTo: date,
        status: ACTIVE_JOB_STATUSES,
    }
}

export function useDashboardData() {
    const today = startOfToday()
    const tomorrow = addDays(today, 1)
    const yesterday = addDays(today, -1)

    const jobsTodayQuery = useJobs(jobFiltersForDay(today))
    const jobsTomorrowQuery = useJobs(jobFiltersForDay(tomorrow))
    const jobsYesterdayQuery = useJobs(jobFiltersForDay(yesterday))
    const fleetQuery = useFleetUtilization()

    const jobsTodayCount = jobsTodayQuery.data?.length ?? 0
    const jobsTomorrowCount = jobsTomorrowQuery.data?.length ?? 0
    const jobsYesterdayCount = jobsYesterdayQuery.data?.length ?? 0

    const jobsTodayComparisonReady =
        !jobsTodayQuery.isLoading &&
        !jobsYesterdayQuery.isLoading &&
        !jobsTodayQuery.isError &&
        !jobsYesterdayQuery.isError
    const jobsTomorrowComparisonReady =
        !jobsTomorrowQuery.isLoading &&
        !jobsTodayQuery.isLoading &&
        !jobsTomorrowQuery.isError &&
        !jobsTodayQuery.isError

    const jobsTodayComparison = jobsTodayComparisonReady
        ? computeDayOverDayComparison(jobsTodayCount, jobsYesterdayCount)
        : undefined
    const jobsTomorrowComparison = jobsTomorrowComparisonReady
        ? computeDayOverDayComparison(jobsTomorrowCount, jobsTodayCount)
        : undefined

    const statCards: DashboardStatCardData[] = [
        {
            title: 'Jobs today',
            value: jobsTodayQuery.isLoading ? null : String(jobsTodayCount),
            comparison: jobsTodayComparison,
            comparisonLabel: 'compared to yesterday',
            isLoading: jobsTodayQuery.isLoading,
            isError: jobsTodayQuery.isError,
            error: jobsTodayQuery.error,
            onRetry: () => void jobsTodayQuery.refetch(),
        },
        {
            title: 'Jobs scheduled for tomorrow',
            value: jobsTomorrowQuery.isLoading ? null : String(jobsTomorrowCount),
            comparison: jobsTomorrowComparison,
            comparisonLabel: 'compared to today',
            isLoading: jobsTomorrowQuery.isLoading,
            isError: jobsTomorrowQuery.isError,
            error: jobsTomorrowQuery.error,
            onRetry: () => void jobsTomorrowQuery.refetch(),
        },
    ]

    return {
        statCards,
        workers: fleetQuery.data?.workers ?? [],
        equipment: fleetQuery.data?.equipment ?? [],
        fleetQuery,
    }
}
